import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { QRCode } from '../components/QRCode'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { EmailVerificationBanner } from '../components/EmailVerificationBanner'
import { generateApplePassPNG, downloadFile } from '../lib/utils'
import toast from 'react-hot-toast'
import { LogOut, Download, Wallet, Star } from 'lucide-react'
import Footer from '../components/Footer'

export default function CustomerDashboard() {
  const { user, firebaseUser, signOut } = useAuth()
  const [isGeneratingPass, setIsGeneratingPass] = useState(false)
  const [isAddingToWallet, setIsAddingToWallet] = useState(false)

  if (!user) return null

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (error) {
      toast.error('Failed to sign out')
    }
  }

  const handleAddToGoogleWallet = async () => {
    setIsAddingToWallet(true)
    try {
      // Use real API instead of mock
      const response = await fetch('/api/google-wallet', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await firebaseUser?.getIdToken()}`
        }
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to create wallet pass')
      }

      const { saveUrl } = await response.json()
      
      // Open Google Wallet save URL
      window.open(saveUrl, '_blank')
      toast.success('Google Wallet pass created!')
      
    } catch (error: any) {
      console.error('Google Wallet error:', error)
      toast.error(error.message || 'Failed to add to Google Wallet')
    } finally {
      setIsAddingToWallet(false)
    }
  }

  const handleAddToAppleWallet = async () => {
    setIsGeneratingPass(true)
    try {
      const passImage = await generateApplePassPNG({
        name: user.name,
        code: user.code,
        stamps: user.stamps
      })
      
      downloadFile(passImage, `loyalty-pass-${user.code}.png`)
      toast.success('Pass downloaded! Save to Photos, then add to Wallet')
    } catch (error) {
      toast.error('Failed to generate pass')
    } finally {
      setIsGeneratingPass(false)
    }
  }

  const stampsNeeded = 6 - user.stamps
  const progress = (user.stamps / 6) * 100

  return (
    <div className="min-h-screen bg-dark-50">
      {/* Header */}
      <header className="bg-dark-100 shadow-sm border-b border-dark-200">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-vapour-900">
            Welcome, {user.name.split(' ')[0]}
          </h1>
          <button
            onClick={handleSignOut}
            className="p-2 text-vapour-600 hover:text-vapour-900"
            aria-label="Sign out"
          >
            <LogOut size={20} />
          </button>
        </div>
      </header>

      <main className="max-w-md mx-auto p-4 space-y-6">
        {/* Email Verification Banner */}
        <EmailVerificationBanner />
        
        {/* QR Code Card */}
        <div className="bg-dark-100 rounded-xl shadow-sm border border-dark-200 p-6 text-center">
          <h2 className="text-lg font-semibold text-vapour-900 mb-4">
            Your Loyalty Code
          </h2>
          
          <div className="flex items-center justify-center p-4 bg-white rounded-lg border-2 border-dark-300 mb-4">
            <QRCode value={user.code} size={180} />
          </div>
          
          <div className="text-2xl font-bold text-vapour-600 mb-2">
            {user.code}
          </div>
          
          <p className="text-sm text-vapour-600">
            Show this QR code or number to staff to earn stamps
          </p>
        </div>

        {/* Progress Card */}
        <div className="bg-dark-100 rounded-xl shadow-sm border border-dark-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-vapour-900">
              Stamp Progress
            </h3>
            <div className="flex items-center text-vapour-600">
              <Star size={20} className="mr-1" />
              <span className="font-bold">{user.stamps}/6</span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-dark-300 rounded-full h-3 mb-4">
            <div 
              className="bg-vapour-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Stamp Circles */}
          <div className="flex justify-between mb-4">
            {Array.from({ length: 6 }, (_, i) => (
              <div
                key={i}
                className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                  i < user.stamps
                    ? 'bg-vapour-600 border-vapour-600 text-white'
                    : 'border-dark-400 text-dark-400'
                }`}
              >
                <Star size={16} fill={i < user.stamps ? 'white' : 'none'} />
              </div>
            ))}
          </div>

          <div className="text-center">
            {user.stamps >= 6 ? (
              <p className="text-green-400 font-semibold">
                🎉 You've earned a free reward! Show this to staff to redeem.
              </p>
            ) : (
              <p className="text-vapour-600">
                {stampsNeeded} more stamp{stampsNeeded !== 1 ? 's' : ''} until your next reward
              </p>
            )}
          </div>
        </div>

        {/* Wallet Integration */}
        <div className="bg-dark-100 rounded-xl shadow-sm border border-dark-200 p-6">
          <h3 className="text-lg font-semibold text-vapour-900 mb-4">
            Add to Wallet
          </h3>
          
          <div className="space-y-3">
            <button
              onClick={handleAddToGoogleWallet}
              disabled={isAddingToWallet}
              className="w-full bg-vapour-600 hover:bg-vapour-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isAddingToWallet ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Adding to Google Wallet...
                </>
              ) : (
                <>
                  <Wallet size={20} className="mr-2" />
                  Add to Google Wallet
                </>
              )}
            </button>

            <button
              onClick={handleAddToAppleWallet}
              disabled={isGeneratingPass}
              className="w-full bg-dark-200 hover:bg-dark-300 text-vapour-900 font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
            >
              {isGeneratingPass ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Generating Pass...
                </>
              ) : (
                <>
                  <Download size={20} className="mr-2" />
                  Download for Apple Wallet
                </>
              )}
            </button>
          </div>

          <p className="text-xs text-vapour-500 mt-3 text-center">
            Apple users: Save the downloaded image to Photos, then add to Wallet
          </p>
        </div>

        {/* Demo Info */}
        <div className="bg-vapour-100 border border-vapour-200 rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-vapour-900 mb-2">
            🎯 How to Earn Stamps
          </h3>
          <p className="text-sm text-vapour-700">
            Show your QR code or tell staff your code when making a purchase to earn stamps. Collect 6 stamps to unlock your free reward!
            Your unique code is <strong>{user.code}</strong>.
          </p>
        </div>
      </main>
      
      <Footer />
    </div>
  )
}